import React, { useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FiShield } from "react-icons/fi"
import { SpinnerText } from "../ui/Spinner"
import SectionHeader from "../ui/SectionHeader"
import BackupCodes from "./BackupCodes"
import TwoFactorEnroll from "./TwoFactorEnroll"
import { RootState } from "../../store/store"
import { updateUser } from "../../slices/authSlice"
import {
  EnrollmentApi,
  TwoFactorMethod,
  disableTwoFactor,
  enableTwoFactor,
  sendTwoFactorCode,
  startTwoFactorSetup
} from "../../services/twoFactorService"

const inputStyle =
  "w-full rounded-lg border border-secondary4 px-4 py-2.5 text-sm focus:border-dantzOrange focus:outline-none focus:ring-2 focus:ring-dantzOrange/20"
const primaryButton =
  "rounded-lg bg-dantzOrange px-6 py-2.5 font-medium text-white transition hover:brightness-110 active:scale-[0.98] disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dantzBlue"
const secondaryButton =
  "rounded-lg px-4 py-2.5 text-sm text-secondary4 transition hover:text-dantzBlue"

type Step = "idle" | "enroll" | "backup" | "password" | "disable"

const METHOD_NAMES: Record<TwoFactorMethod, string> = {
  app: "authenticator app",
  email: "code by e-mail"
}

export default function TwoFactorForm() {
  const dispatch = useDispatch()
  const user = useSelector((state: RootState) => state.auth.user)
  const refreshToken = useSelector((state: RootState) => state.auth.refreshToken)
  const enabled = Boolean(user?.twoFactorEnabled)
  const method: TwoFactorMethod = user?.twoFactorMethod === "email" ? "email" : "app"
  const mandatory = Boolean(user?.twoFactorMandatory)

  const [step, setStep] = useState<Step>("idle")
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  // Changing the method asks for the password once, then it is used to start
  const [password, setPassword] = useState("")
  const [changing, setChanging] = useState(false)
  const [code, setCode] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const [notice, setNotice] = useState("")

  const reset = () => {
    setStep("idle")
    setCode("")
    setPassword("")
    setChanging(false)
    setError("")
  }

  const run = async (action: () => Promise<void>, fallback: string) => {
    setBusy(true)
    setError("")
    try {
      await action()
    } catch (err: any) {
      setError(err?.message || fallback)
    } finally {
      setBusy(false)
    }
  }

  const enrollApi: EnrollmentApi = useMemo(
    () => ({
      start: (chosen) => startTwoFactorSetup(chosen, changing ? password : undefined),
      finish: async (chosen, enteredCode) => {
        const result = await enableTwoFactor(chosen, enteredCode, refreshToken)
        dispatch(updateUser({ twoFactorEnabled: true, twoFactorMethod: chosen }))
        return result
      }
    }),
    [changing, password, refreshToken, dispatch]
  )

  const handleEnrolled = (result: { backupCodes: string[] }) => {
    setPassword("")
    setChanging(false)

    if (result.backupCodes.length > 0) {
      setBackupCodes(result.backupCodes)
      setStep("backup")
    } else {
      reset()
      setNotice("Two-factor authentication is on. You were signed out on all other devices.")
    }
  }

  const openDisable = () =>
    run(async () => {
      setNotice("")
      setStep("disable")
      if (method === "email") {
        // A code sent a moment ago is still valid, so a failure here is not final
        await sendTwoFactorCode().catch(() => undefined)
      }
    }, "")

  const handleDisable = (e: React.FormEvent) => {
    e.preventDefault()
    return run(async () => {
      await disableTwoFactor(password, code.trim())
      dispatch(updateUser({ twoFactorEnabled: false, twoFactorMethod: "app" }))
      reset()
      setNotice("Two-factor authentication is turned off.")
    }, "Two-factor authentication could not be turned off. Please try again.")
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <SectionHeader
        icon={<FiShield size={20} />}
        title="Two-factor authentication"
        subtitle="Add a second step to your login."
      />

      {step === "idle" && !enabled && (
        <div>
          <p className="text-sm text-gray-600">
            When it is on, you enter a 6-digit code after your password. Your
            account stays protected even if the password is stolen.
          </p>
          <div className="mt-5">
            <button
              type="button"
              onClick={() => {
                setNotice("")
                setStep("enroll")
              }}
              className={primaryButton}
            >
              Set up
            </button>
          </div>
        </div>
      )}

      {step === "idle" && enabled && (
        <div>
          <p className="text-sm text-green-700">
            Two-factor authentication is on ({METHOD_NAMES[method]}).
          </p>
          {mandatory && (
            <p className="mt-2 text-sm text-gray-600">
              It is mandatory and cannot be turned off.
            </p>
          )}
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setNotice("")
                setStep("password")
              }}
              className="rounded-lg border border-secondary4 px-6 py-2.5 text-sm font-medium text-dantzBlue transition hover:bg-gray-50"
            >
              Change method
            </button>
            {!mandatory && (
              <button
                type="button"
                onClick={openDisable}
                className="rounded-lg border border-red-300 px-6 py-2.5 text-sm font-medium text-red-600 transition hover:bg-red-50"
              >
                Turn off
              </button>
            )}
          </div>
        </div>
      )}

      {step === "password" && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            setChanging(true)
            setStep("enroll")
          }}
          className="flex flex-col gap-4"
        >
          <p className="text-sm text-gray-600">
            Enter your password to change the method. The current one stops
            working as soon as the new one is on.
          </p>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Password *
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputStyle}
            />
          </div>
          <div className="flex items-center gap-2">
            <button type="submit" disabled={!password} className={primaryButton}>
              Continue
            </button>
            <button type="button" onClick={reset} className={secondaryButton}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {step === "enroll" && (
        <TwoFactorEnroll
          api={enrollApi}
          appOnly={Boolean(user?.twoFactorAppRequired)}
          onDone={handleEnrolled}
          onCancel={reset}
        />
      )}

      {step === "backup" && (
        <BackupCodes
          codes={backupCodes}
          doneLabel="I have saved them"
          note="You were signed out on all other devices."
          onDone={() => {
            setBackupCodes([])
            reset()
          }}
        />
      )}

      {step === "disable" && (
        <form onSubmit={handleDisable} className="flex flex-col gap-4">
          <p className="text-sm text-gray-600">
            {method === "email"
              ? "Enter your password and the code we sent to your e-mail address to turn it off."
              : "Enter your password and a current code (or a backup code) to turn it off."}
          </p>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Password *
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                setError("")
              }}
              className={inputStyle}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Code *
            </label>
            <input
              type="text"
              autoComplete="one-time-code"
              maxLength={12}
              value={code}
              onChange={(e) => {
                setCode(e.target.value)
                setError("")
              }}
              className={inputStyle}
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={busy || !password || !code.trim()}
              className="rounded-lg bg-red-600 px-6 py-2.5 font-medium text-white transition hover:bg-red-700 disabled:opacity-60"
            >
              {busy ? <SpinnerText>Turning off…</SpinnerText> : "Turn off"}
            </button>
            <button type="button" onClick={reset} className={secondaryButton}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {error && step !== "enroll" && (
        <div className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
          {error}
        </div>
      )}
      {notice && (
        <div className="mt-4 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
          {notice}
        </div>
      )}
    </div>
  )
}
